import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  HeartHandshake, 
  PhoneCall, 
  ChevronDown, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  Calendar, 
  User, 
  MapPin, 
  Award, 
  ArrowRight, 
  FileText, 
  HelpCircle,
  Building2,
  Lock,
  Globe2,
  DollarSign
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE_DISPLAY, SITE_DOMAIN } from '../components/SEOHead';
import CallbackForm from '../components/CallbackForm';
import CallbackModal from '../components/CallbackModal';
import { trackCallbackClick } from '../services/analytics';

const SECTIONS = [
  { id: 'introduction', title: 'Why NRIs Need a 2026 Insurance Review' },
  { id: 'health-parents', title: 'Health Insurance for Parents in India' },
  { id: 'optima-secure-plus', title: '1. HDFC ERGO Optima Secure+' },
  { id: 'optima-secure', title: '2. HDFC ERGO Optima Secure' },
  { id: 'super-topup', title: '3. HDFC ERGO Medisure Super Top-up' },
  { id: 'insurance-nris', title: 'Life & Medical Cover for NRIs Abroad' },
  { id: 'lic-yuva-term', title: '4. LIC Yuva Term (Term Life)' },
  { id: 'optima-global', title: '5. HDFC ERGO Optima Secure Global' },
  { id: 'comparison-matrix', title: 'Summary Policy Comparison (INR & USD)' },
  { id: 'frequently-asked-questions', title: '10 Questions NRIs Often Ask' },
  { id: 'meet-advisor', title: 'Talk to V. S. Reddy (25+ Yrs Exp)' },
];

const FAQS = [
  {
    q: 'Can NRIs buy life insurance policies in India?',
    a: 'Yes. Non-Resident Indians (NRIs), Persons of Indian Origin (PIOs), and Overseas Citizens of India (OCIs) can legally purchase life and term insurance policies issued by Indian insurers. Policies can be completed either during a visit to India or from abroad via Mail Order Business (MOB) through authorized advisors. Premiums can be funded via NRE, NRO, or foreign currency bank accounts, and proceeds for NRE-funded policies are freely repatriable under FEMA rules.'
  },
  {
    q: 'Can I purchase health insurance for my parents from the USA?',
    a: 'Yes, absolutely. As an NRI living in the USA, Canada, UK, or anywhere abroad, you can propose and pay for comprehensive health insurance covering your parents residing in India. Payment can be made using foreign credit cards or an Indian NRE/NRO account. This allows you to guarantee cashless treatment for your parents in top hospital networks across India.'
  },
  {
    q: 'Which health insurance should I consider for parents over 50?',
    a: 'For parents aged 50 and older, key factors to consider include absence of room-rent capping, restore/multiplier benefits to counter medical inflation, reasonable pre-existing disease waiting periods, and day-care treatment coverage. Plans like HDFC ERGO Optima Secure or Optima Secure+ provide high base coverage with automatic growth multipliers that protect family wealth against major hospitalizations.'
  },
  {
    q: 'What is a super top-up policy?',
    a: 'A super top-up policy is cost-effective health coverage that activates once your cumulative (aggregate) medical expenses across an entire policy year exceed a chosen threshold known as the "deductible." Unlike a basic top-up (which triggers only on a single hospital bill), a super top-up tracks all hospitalizations in that year. It is one of the most budget-friendly ways for NRIs to expand their parents’ safety net from ₹5 Lakhs to ₹25+ Lakhs.'
  },
  {
    q: 'Does Indian health insurance cover medical treatment in America?',
    a: 'Standard Indian retail health policies only cover hospitalizations within India. Certain global variants (such as Optima Secure Global) provide emergency overseas medical benefits or coverage for specified planned treatments abroad under strict eligibility criteria and deductibles. However, Indian global riders are NOT a replacement for comprehensive US or local employer-sponsored medical insurance.'
  },
  {
    q: 'Can an NRI buy LIC Yuva Term?',
    a: 'LIC Yuva Term is an individual, non-par pure term insurance plan designed for younger breadwinners. NRI eligibility depends on country of residence, nationality, income underwriting, and medical verification. An insurance advisor reviews whether your current country of residence falls under approved territorial zones before submitting proposal paperwork.'
  },
  {
    q: 'Can I compare multiple policies before purchasing?',
    a: 'Yes. Comparing policies based on your parents’ exact ages, pre-existing health conditions, and city of residence is the most prudent step. VSR Insurance Plus conducts transparent, conflict-free comparisons across sovereign-backed LIC policies and HDFC ERGO health plans without sales pressure.'
  },
  {
    q: 'Will insurance cover pre-existing diseases immediately?',
    a: 'No. Standard health insurance policies in India have statutory waiting periods ranging from 24 to 36 months for declared pre-existing ailments (such as hypertension or diabetes), unless a specific reduction rider is approved. Full, truthful disclosure of medical records during application is mandatory to ensure stress-free claim settlement later.'
  },
  {
    q: 'What documents may be required for NRIs?',
    a: 'Typically: valid passport copy, visa/work permit, foreign address proof, overseas employment verification or bank statements, and Indian PAN card. For policies completed via Mail Order Business abroad, notary or Indian Embassy attestation may be requested depending on the coverage amount.'
  },
  {
    q: 'How can I speak with an insurance advisor in India?',
    a: 'You can request a direct callback through our secure callback form on this page or schedule an appointment with Senior Advisor V. S. Reddy. Consultations are scheduled around international time zones for NRIs in North America, Europe, Australia, and the Gulf.'
  }
];

const NRIInsuranceLeadBlog = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currencyMode, setCurrencyMode] = useState('INR'); // 'INR' or 'USD'

  // Scroll spy for sticky table of contents
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 160;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPos >= top && scrollPos < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openCallbackModal = (locationName) => {
    trackCallbackClick(locationName);
    setIsModalOpen(true);
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': 'Best Health & Life Insurance for NRIs and Their Families in 2026',
    'description': 'Compare health insurance for parents in India and life insurance options for NRIs. Explore benefits, estimated premiums and request a callback from V. S. Reddy.',
    'image': `${SITE_DOMAIN}/og-image.png`,
    'datePublished': '2026-01-20',
    'dateModified': '2026-10-24',
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${SITE_DOMAIN}/blog/best-insurance-for-nris-and-parents`
    },
    'author': {
      '@type': 'Person',
      'name': ADVISOR_NAME,
      'jobTitle': 'Senior Insurance Advisor',
      'url': `${SITE_DOMAIN}/about`
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'VSR Insurance Plus',
      'logo': {
        '@type': 'ImageObject',
        'url': `${SITE_DOMAIN}/logo.png`
      }
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': FAQS.map(f => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': f.a
      }
    }))
  };

  return (
    <div className="min-h-screen pt-24 pb-28 bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200">
      <SEOHead
        title="Best Health & Life Insurance for NRIs and Their Families in 2026"
        description="Compare health insurance for parents in India and life insurance options for NRIs. Explore benefits, estimated premiums and request a callback from V. S. Reddy."
        canonicalPath="/blog/best-insurance-for-nris-and-parents"
        schemaJson={[articleSchema, faqSchema]}
        breadcrumbs={[
          { name: 'Insurance Guides', path: '/blog' },
          { name: 'Best Insurance for NRIs & Parents', path: '/blog/best-insurance-for-nris-and-parents' }
        ]}
      />

      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        {/* Breadcrumb Navigation */}
        <nav className="text-xs text-slate-500 mb-6 flex items-center gap-1.5" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-primary">Guides</Link>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-300 font-medium truncate">Best Insurance for NRIs & Parents</span>
        </nav>

        {/* Hero Header */}
        <header className="max-w-4xl mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-primary text-xs font-bold uppercase tracking-wider mb-4">
            <Globe2 className="w-3.5 h-3.5" /> NRI Financial & Healthcare Advisory • 2026 Edition
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-[1.18] tracking-tight mb-6">
            Best Health & Life Insurance for NRIs and Their Families in 2026
          </h1>
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-6 font-normal">
            A comprehensive, conflict-free guide for Non-Resident Indians seeking reliable medical coverage for aging parents in India and strategic life cover for breadwinners abroad.
          </p>

          <div className="flex flex-wrap items-center gap-4 py-3 border-y border-slate-200 dark:border-slate-800 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs">
                VSR
              </div>
              <div>
                <span className="font-semibold text-slate-800 dark:text-slate-200">{ADVISOR_NAME}</span>
                <span className="block text-[11px] text-slate-400">Senior Insurance Advisor (25+ Yrs Exp)</span>
              </div>
            </div>
            <span className="hidden sm:inline text-slate-300">•</span>
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Published: Jan 2026
            </div>
            <span className="hidden sm:inline text-slate-300">•</span>
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> Reviewed: Oct 2026
            </div>
            <span className="hidden sm:inline text-slate-300">•</span>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> 11 min read
            </div>
          </div>
        </header>

        {/* Main Content Grid: Sidebar + Article */}
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Sticky Desktop Table of Contents */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" /> Table of Contents
              </h2>
              <nav className="space-y-1">
                {SECTIONS.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    className={`block px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                      activeSection === sec.id
                        ? 'bg-primary/10 text-primary font-bold translate-x-1'
                        : 'text-slate-600 dark:text-slate-400 hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    }`}
                  >
                    {sec.title}
                  </a>
                ))}
              </nav>

              {/* Sidebar Quick Advisor Card */}
              <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800 space-y-3 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-primary to-blue-600 text-white font-black text-lg flex items-center justify-center mx-auto shadow-md">
                  VSR
                </div>
                <h4 className="font-bold text-slate-900 dark:text-white text-sm">Have Specific Questions?</h4>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Speak directly with V. S. Reddy. We match your parents' medical history to the optimal policy structure.
                </p>
                <button
                  onClick={() => openCallbackModal('desktop_sidebar')}
                  className="w-full py-3 px-4 bg-primary hover:bg-blue-600 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-primary/20 flex items-center justify-center gap-2 cursor-pointer"
                >
                  <PhoneCall className="w-3.5 h-3.5" /> Request a Callback
                </button>
              </div>
            </div>
          </aside>

          {/* Article Main Body */}
          <article className="lg:col-span-8 space-y-12 leading-relaxed text-slate-700 dark:text-slate-300">
            
            {/* SECTION: INTRODUCTION */}
            <section id="introduction" className="scroll-mt-28 space-y-5">
              <div className="p-6 md:p-8 bg-blue-50/70 dark:bg-blue-950/30 rounded-3xl border border-blue-100 dark:border-blue-900/50">
                <p className="text-base sm:text-lg text-slate-800 dark:text-slate-200 leading-relaxed font-medium">
                  "Living in the USA, Canada, UK, or another country? While you're building your future abroad, making sure your parents in India have appropriate health insurance—and protecting your own family's financial future—is equally important."
                </p>
              </div>

              <p>
                As an Indian professional living overseas, thousands of miles away from home, managing healthcare contingencies for aging parents is one of the greatest emotional and financial stressors. A single critical illness, joint replacement, or cardiac intervention in premier hospital chains across Hyderabad, Bengaluru, Mumbai, or Delhi can easily generate bills exceeding ₹15 Lakhs to ₹30 Lakhs ($16,000 to $32,000).
              </p>

              <p>
                Without a dedicated, high-quality private health insurance policy in India, NRIs frequently face severe financial disruptions, urgent cross-border wire remittances, and chaotic hospital admission negotiations during medical emergencies. Concurrently, many young NRI breadwinners neglect sovereign-backed Indian life cover, mistakenly assuming local foreign employer term insurance alone will safeguard their dependents across international jurisdictions.
              </p>

              <p>
                In this educational review, we break down five widely evaluated policies—three parental health insurance vehicles and two individual protection plans for NRIs abroad—highlighting real coverage mechanics, realistic budgeting illustrations, waiting periods, and common pitfalls.
              </p>
            </section>

            {/* SECTION A: HEALTH INSURANCE FOR PARENTS */}
            <section id="health-parents" className="scroll-mt-28 space-y-6 pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-primary text-white font-extrabold text-sm flex items-center justify-center">A</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Health Insurance for Parents in India
                </h2>
              </div>
              <p>
                When selecting medical coverage for parents aged 50 to 75 in India, three operational features matter far more than marketing headlines: <strong>zero room-rent sub-limits</strong> (preventing proportional billing deductions), <strong>hospital cash restoration</strong>, and <strong>pre-existing disease (PED) terms</strong>.
              </p>

              {/* 1. Optima Secure+ */}
              <div id="optima-secure-plus" className="scroll-mt-28 bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-primary text-xs font-bold rounded-full uppercase">
                    Comprehensive Health
                  </span>
                  <div className="text-xs font-semibold text-slate-500">
                    Parent Age 50–60 Estimate: <strong className="text-slate-900 dark:text-white">₹25,000–₹55,000 ($263–$579)/yr</strong>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  1. HDFC ERGO Optima Secure+
                </h3>

                <p className="text-sm">
                  <strong>General Coverage Structure:</strong> Optima Secure+ is an advanced retail indemnity health insurance policy engineered to defeat high medical inflation. It eliminates common out-of-pocket deductions by providing comprehensive inpatient hospitalization coverage without arbitrary room rent ceilings or ICU caps.
                </p>

                <div className="space-y-2 text-sm">
                  <h4 className="font-bold text-slate-900 dark:text-white">Coverage-Related Benefits:</h4>
                  <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    <li><strong>Secure Benefit:</strong> Provides 2X base cover starting from Day 1 at no extra cost (e.g., buying a ₹10 Lakhs base policy yields an instant ₹20 Lakhs coverage pool).</li>
                    <li><strong>Plus Benefit:</strong> Multiplies your base sum insured by 100% after two consecutive claim-free policy years, effectively doubling base coverage to 3X or 4X.</li>
                    <li><strong>Automatic Restoration:</strong> Offers 100% restoration of the base sum insured whenever claims deplete the balance, usable for unrelated medical conditions in the same policy period.</li>
                    <li><strong>Zero Consumable Bills:</strong> Covers normally non-payable hospitalization consumables such as PPE kits, gloves, surgical masks, and bandages.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-2xl space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <p><strong>Waiting Periods:</strong> Standard 30-day initial waiting period (except accidental trauma). 24-month waiting period for specified ailments (cataract, hernia, joint replacements). 24 to 36 months for pre-existing medical conditions.</p>
                  <p><strong>Exclusions & Limitations:</strong> Routine dental procedures, cosmetic surgeries, non-allopathic treatments outside certified hospitals, and experimental treatments are excluded.</p>
                  <p><strong>Who Should Compare It:</strong> NRIs seeking maximum day-1 coverage buffers for parents with minimal willingness to pay unexpected hospital consumable bills.</p>
                </div>
              </div>

              {/* 2. Optima Secure */}
              <div id="optima-secure" className="scroll-mt-28 bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 text-xs font-bold rounded-full uppercase">
                    Proven Flagship
                  </span>
                  <div className="text-xs font-semibold text-slate-500">
                    Parent Age 50–60 Estimate: <strong className="text-slate-900 dark:text-white">₹20,000–₹45,000 ($211–$474)/yr</strong>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  2. HDFC ERGO Optima Secure
                </h3>

                <p className="text-sm">
                  <strong>Hospitalization Coverage:</strong> HDFC ERGO's foundational Optima Secure plan provides cashless hospitalization across 14,000+ accredited network hospitals in India. It includes 60 days of pre-hospitalization and 180 days of post-hospitalization medical expenses.
                </p>

                <div className="space-y-2 text-sm">
                  <h4 className="font-bold text-slate-900 dark:text-white">Key Differences from Secure+:</h4>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    While Optima Secure includes the signature 2X Day-1 Secure Benefit and Restore Benefit, it generally features slightly different deductible thresholds and rider packages compared to the enhanced Secure+ configuration. Premiums for Optima Secure are approximately 10% to 15% lower, making it attractive for budget-conscious families whose parents have relatively clean recent medical checks.
                  </p>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-2xl space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <p><strong>Limitations & Eligibility:</strong> Entry age up to 65 years without upper exit limit. Pre-policy medical tele-underwriting or physical checkups may be mandated based on declared history.</p>
                </div>
              </div>

              {/* 3. Medisure Super Top-up */}
              <div id="super-topup" className="scroll-mt-28 bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-400 text-xs font-bold rounded-full uppercase">
                    High-Value Buffer
                  </span>
                  <div className="text-xs font-semibold text-slate-500">
                    Parent Age 50–60 Estimate: <strong className="text-slate-900 dark:text-white">₹5,000–₹15,000 ($53–$158)/yr</strong>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  3. HDFC ERGO Medisure Super Top-up
                </h3>

                <p className="text-sm">
                  <strong>How an Aggregate Deductible Super Top-up Works:</strong> Many parents already hold a small existing policy (e.g. ₹3 Lakhs or ₹5 Lakhs from a former employer or state cooperative). Rather than purchasing an expensive brand-new ₹25 Lakhs primary plan, an NRI can purchase a Super Top-up with a ₹5 Lakhs deductible.
                </p>

                <div className="space-y-2 text-sm">
                  <h4 className="font-bold text-slate-900 dark:text-white">Deductible Mechanics in Action:</h4>
                  <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 rounded-2xl border border-amber-200/50 dark:border-amber-900/40 text-xs text-slate-700 dark:text-slate-300 space-y-1.5">
                    <p>• <strong>Hospitalization 1:</strong> Hospital bill equals ₹3,50,000. Paid entirely by your existing base insurance. (Deductible balance remaining: ₹1,50,000).</p>
                    <p>• <strong>Hospitalization 2:</strong> Same year, another hospital bill arrives for ₹8,00,000. The remaining ₹1,50,000 deductible is satisfied, and Medisure Super Top-up automatically pays the remaining ₹6,50,000!</p>
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-2xl space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <p><strong>Common Limitations:</strong> Does not pay any expense below the cumulative threshold. Medical expenses must qualify under eligible hospitalization definitions.</p>
                  <p><strong>Who Should Consider It:</strong> Excellent, high-leverage option to multiply parents' total protection pool to ₹20 Lakhs or ₹30 Lakhs for a fraction of primary insurance costs.</p>
                </div>
              </div>

              {/* Pricing Disclaimer Box */}
              <div className="p-4 bg-slate-100 dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-800 text-xs text-slate-500 italic">
                <strong>Required Pricing Disclaimer:</strong> All prices are broad, unverified budgeting illustrations, not actual insurer quotations. They do not assume identical coverage amounts, family composition, medical history or location. Exact underwriting quotations require individual evaluation.
              </div>
            </section>

            {/* SECTION B: INSURANCE FOR NRIs LIVING ABROAD */}
            <section id="insurance-nris" className="scroll-mt-28 space-y-6 pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-primary text-white font-extrabold text-sm flex items-center justify-center">B</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Insurance for NRIs Living Abroad
                </h2>
              </div>
              <p>
                Living outside India does not eliminate family financial responsibilities back home. Indian breadwinners abroad frequently need sovereign-guaranteed term protection for dependents in India and emergency medical coverage when visiting multiple countries.
              </p>

              {/* 4. LIC Yuva Term */}
              <div id="lic-yuva-term" className="scroll-mt-28 bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-primary text-xs font-bold rounded-full uppercase">
                    Pure Term Life
                  </span>
                  <div className="text-xs font-semibold text-slate-500">
                    Age 25–30 Estimate: <strong className="text-slate-900 dark:text-white">₹8,000–₹18,000 ($84–$190)/yr</strong>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  4. LIC Yuva Term
                </h3>

                <p className="text-sm">
                  <strong>What Pure Term Life Insurance Provides:</strong> LIC Yuva Term is a non-par, non-linked individual pure risk protection plan designed for younger breadwinners. Unlike investment or endowment policies that return maturity sums, pure term insurance exists solely to pay a massive, contractually guaranteed tax-free lump sum to your nominated family members in case of your untimely demise during the policy tenure.
                </p>

                <div className="space-y-2 text-sm">
                  <h4 className="font-bold text-slate-900 dark:text-white">Why NRIs Consider Term Protection in India:</h4>
                  <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    <li><strong>Sovereign Guarantee:</strong> Guaranteed under Section 37 of the LIC Act by the Government of India, offering unmatched generational stability.</li>
                    <li><strong>Cost Efficiency:</strong> Annual premiums for Indian term policies are frequently lower than private North American or European retail policies for comparable coverage.</li>
                    <li><strong>FEMA Repatriation:</strong> Death benefits or maturity proceeds paid from NRE premium contributions are completely repatriable under Reserve Bank of India / FEMA regulations.</li>
                    <li><strong>Crucial Note:</strong> Term insurance is pure financial life protection—it does NOT provide hospitalization or medical reimbursement benefits.</li>
                  </ul>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-2xl text-xs text-slate-600 dark:text-slate-400">
                  <p><strong>Underwriting & Residence Clarification:</strong> NRI eligibility must be individually verified based on residency, nationality, policy terms, location and insurer requirements. Certain overseas countries fall under standard underwriting zones, while others may require specific Mail Order Business questionnaires.</p>
                </div>
              </div>

              {/* 5. Optima Secure Global */}
              <div id="optima-global" className="scroll-mt-28 bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 text-xs font-bold rounded-full uppercase">
                    Worldwide Hospitalization
                  </span>
                  <div className="text-xs font-semibold text-slate-500">
                    Illustrative Estimate: <strong className="text-slate-900 dark:text-white">₹15,000–₹35,000 ($158–$369)/yr</strong>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  5. HDFC ERGO Optima Secure Global
                </h3>

                <p className="text-sm">
                  <strong>Specified Worldwide Medical Benefits:</strong> Designed for globe-trotting Indian residents and eligible NRIs traveling frequently between India and abroad. It extends base hospitalization protection beyond Indian borders to cover emergency medical treatments in accredited international facilities.
                </p>

                <div className="space-y-2 text-sm">
                  <h4 className="font-bold text-slate-900 dark:text-white">Global vs Global Plus Nuances:</h4>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    While basic Global variants often exclude the USA and Canada due to elevated healthcare tariffs, Global Plus riders allow pre-planned or emergency hospitalizations in North America, subject to specific co-payments or deductibles.
                  </p>
                </div>

                <div className="p-4 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900/50 text-xs text-rose-800 dark:text-rose-300">
                  <p className="font-bold mb-1">Important Healthcare Notice:</p>
                  <p>This policy must not be represented as automatic comprehensive USA health insurance or a replacement for employer-sponsored or student health insurance. Overseas residents must maintain primary health insurance compliant with local domestic mandates.</p>
                </div>
              </div>
            </section>

            {/* SECTION C: COMPARISON SUMMARY TABLE */}
            <section id="comparison-matrix" className="scroll-mt-28 space-y-6 pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-primary text-white font-extrabold text-sm flex items-center justify-center">C</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Comparison Summary of All Five Policies
                </h2>
              </div>
              <p>
                Below is a side-by-side comparison of the five reviewed insurance vehicles. Toggle between Indian Rupees (₹) and US Dollars ($) to evaluate indicative budgeting figures.
              </p>

              {/* Currency Toggle */}
              <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Display Estimates In:</span>
                <div className="inline-flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                  <button
                    onClick={() => setCurrencyMode('INR')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      currencyMode === 'INR' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    INR (₹)
                  </button>
                  <button
                    onClick={() => setCurrencyMode('USD')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                      currencyMode === 'USD' ? 'bg-primary text-white shadow-sm' : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    USD ($) @ ₹94.95
                  </button>
                </div>
              </div>

              {/* Desktop Responsive Table */}
              <div className="hidden md:block overflow-x-auto bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-200 font-bold border-b border-slate-200 dark:border-slate-800">
                      <th className="p-4">Policy Name</th>
                      <th className="p-4">Category</th>
                      <th className="p-4">Intended Purpose</th>
                      <th className="p-4">Illustrative Premium</th>
                      <th className="p-4">Major Considerations</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="p-4 font-bold text-slate-900 dark:text-white">HDFC ERGO Optima Secure+</td>
                      <td className="p-4 text-slate-500">Health Insurance</td>
                      <td className="p-4">Parental Hospitalization in India</td>
                      <td className="p-4 font-bold text-primary">
                        {currencyMode === 'INR' ? '₹25,000 – ₹55,000 / yr' : '$263 – $579 / yr'}
                      </td>
                      <td className="p-4 text-slate-500">2X Day-1 cover, zero room rent cap, zero consumable deductions</td>
                    </tr>
                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="p-4 font-bold text-slate-900 dark:text-white">HDFC ERGO Optima Secure</td>
                      <td className="p-4 text-slate-500">Health Insurance</td>
                      <td className="p-4">Core Family & Parent Floater</td>
                      <td className="p-4 font-bold text-primary">
                        {currencyMode === 'INR' ? '₹20,000 – ₹45,000 / yr' : '$211 – $474 / yr'}
                      </td>
                      <td className="p-4 text-slate-500">Proven flagship indemnity cover; 100% restoration on exhaustion</td>
                    </tr>
                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="p-4 font-bold text-slate-900 dark:text-white">HDFC ERGO Medisure Super Top-up</td>
                      <td className="p-4 text-slate-500">Super Top-up</td>
                      <td className="p-4">High-Sum Secondary Buffer</td>
                      <td className="p-4 font-bold text-primary">
                        {currencyMode === 'INR' ? '₹5,000 – ₹15,000 / yr' : '$53 – $158 / yr'}
                      </td>
                      <td className="p-4 text-slate-500">Requires aggregate deductible satisfaction; economical layer</td>
                    </tr>
                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="p-4 font-bold text-slate-900 dark:text-white">LIC Yuva Term</td>
                      <td className="p-4 text-slate-500">Pure Term Life</td>
                      <td className="p-4">Family Income Protection</td>
                      <td className="p-4 font-bold text-primary">
                        {currencyMode === 'INR' ? '₹8,000 – ₹18,000 / yr' : '$84 – $190 / yr'}
                      </td>
                      <td className="p-4 text-slate-500">Sovereign guarantee; tax-free repatriable death payout under FEMA</td>
                    </tr>
                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="p-4 font-bold text-slate-900 dark:text-white">HDFC ERGO Optima Secure Global</td>
                      <td className="p-4 text-slate-500">Global Medical</td>
                      <td className="p-4">Worldwide Emergency Inpatient</td>
                      <td className="p-4 font-bold text-primary">
                        {currencyMode === 'INR' ? '₹15,000 – ₹35,000 / yr' : '$158 – $369 / yr'}
                      </td>
                      <td className="p-4 text-slate-500">Specific international benefits; not a US domestic health plan replacement</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards for Table Data */}
              <div className="md:hidden space-y-4">
                {[
                  {
                    name: 'HDFC ERGO Optima Secure+',
                    cat: 'Health Insurance',
                    purpose: 'Parental Hospitalization in India',
                    inr: '₹25,000 – ₹55,000 / yr',
                    usd: '$263 – $579 / yr',
                    note: '2X Day-1 cover, zero room rent cap, zero consumable bills'
                  },
                  {
                    name: 'HDFC ERGO Optima Secure',
                    cat: 'Health Insurance',
                    purpose: 'Core Family & Parent Floater',
                    inr: '₹20,000 – ₹45,000 / yr',
                    usd: '$211 – $474 / yr',
                    note: 'Proven flagship indemnity cover; 100% restoration on exhaustion'
                  },
                  {
                    name: 'HDFC ERGO Medisure Super Top-up',
                    cat: 'Super Top-up',
                    purpose: 'High-Sum Secondary Buffer',
                    inr: '₹5,000 – ₹15,000 / yr',
                    usd: '$53 – $158 / yr',
                    note: 'Requires aggregate deductible satisfaction; economical layer'
                  },
                  {
                    name: 'LIC Yuva Term',
                    cat: 'Pure Term Life',
                    purpose: 'Family Income Protection',
                    inr: '₹8,000 – ₹18,000 / yr',
                    usd: '$84 – $190 / yr',
                    note: 'Sovereign guarantee; tax-free repatriable death payout under FEMA'
                  },
                  {
                    name: 'HDFC ERGO Optima Secure Global',
                    cat: 'Global Medical',
                    purpose: 'Worldwide Emergency Inpatient',
                    inr: '₹15,000 – ₹35,000 / yr',
                    usd: '$158 – $369 / yr',
                    note: 'Specific international benefits; not a US domestic health plan replacement'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
                    <div className="flex justify-between items-start gap-2">
                      <h4 className="font-bold text-slate-900 dark:text-white text-sm">{item.name}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-primary font-semibold whitespace-nowrap">
                        {item.cat}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500"><strong>Purpose:</strong> {item.purpose}</p>
                    <p className="text-xs font-bold text-primary">
                      {currencyMode === 'INR' ? item.inr : item.usd}
                    </p>
                    <p className="text-[11px] text-slate-400">{item.note}</p>
                  </div>
                ))}
              </div>

              {/* Rate & Pricing Disclaimer directly below table */}
              <div className="p-4 bg-slate-100 dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-800 text-xs text-slate-500 space-y-1">
                <p>
                  <strong>Exchange Rate Notice:</strong> Used ₹94.95 = $1 exclusively as the illustrative exchange rate for these supplied estimates, not as a live exchange rate.
                </p>
                <p>
                  <strong>Required Pricing Disclaimer:</strong> All prices are broad, unverified budgeting illustrations, not actual insurer quotations. They do not assume identical coverage amounts, family composition, medical history or location.
                </p>
              </div>

              {/* In-content Callback Action Box */}
              <div className="p-6 md:p-8 bg-gradient-to-br from-slate-900 to-blue-950 text-white rounded-3xl text-center space-y-4 shadow-xl">
                <h3 className="text-xl md:text-2xl font-black">
                  Need a Personalized Calculation for Your Parents?
                </h3>
                <p className="text-slate-300 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
                  Every family’s health profile is unique. Submit your details to receive an exact comparison based on your parents' actual ages and city of residence.
                </p>
                <button
                  onClick={() => openCallbackModal('comparison_table_box')}
                  className="px-8 py-3.5 bg-primary hover:bg-blue-500 text-white rounded-xl text-xs sm:text-sm font-bold shadow-lg transition-transform hover:scale-105 cursor-pointer inline-flex items-center gap-2"
                >
                  <PhoneCall className="w-4 h-4" /> Request a Callback from V. S. Reddy
                </button>
              </div>
            </section>

            {/* SECTION D: QUESTIONS NRIs OFTEN ASK */}
            <section id="frequently-asked-questions" className="scroll-mt-28 space-y-6 pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-primary text-white font-extrabold text-sm flex items-center justify-center">D</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Questions NRIs Often Ask
                </h2>
              </div>
              <p className="text-sm">
                Conservative, accurate answers to the ten most frequent inquiries received from Indian professionals living in North America, Europe, Australia, and the Middle East.
              </p>

              <div className="space-y-3">
                {FAQS.map((faq, idx) => {
                  const isOpen = openFaqIndex === idx;
                  return (
                    <div
                      key={idx}
                      className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden transition-colors"
                    >
                      <button
                        onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                        className="w-full p-5 text-left flex justify-between items-center gap-4 text-sm font-bold text-slate-900 dark:text-white hover:text-primary transition-colors cursor-pointer"
                        aria-expanded={isOpen}
                      >
                        <span>{idx + 1}. {faq.q}</span>
                        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-primary' : ''}`} />
                      </button>

                      {isOpen && (
                        <div className="px-5 pb-5 text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed border-t border-slate-100 dark:border-slate-800 pt-3">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* SECTION E: MEET YOUR INSURANCE ADVISOR & EMBEDDED FORM */}
            <section id="meet-advisor" className="scroll-mt-28 space-y-8 pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-xl bg-primary text-white font-extrabold text-sm flex items-center justify-center">E</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                  Meet Your Insurance Advisor
                </h2>
              </div>

              {/* Advisor Profile Card */}
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row items-center gap-6">
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-tr from-slate-900 to-primary text-white font-black text-2xl sm:text-3xl flex items-center justify-center flex-shrink-0 shadow-lg border-2 border-yellow-400/40">
                  VSR
                </div>

                <div className="space-y-3 text-center md:text-left flex-grow">
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                    Talk to V. S. Reddy
                  </h3>
                  <p className="text-xs font-semibold text-primary uppercase tracking-wider">
                    Senior Insurance Advisor & Financial Consultant • SVR LIC Services
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                    "With over 25 years of insurance industry experience, V. S. Reddy helps individuals and families understand life insurance, health insurance and retirement planning options."
                  </p>
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-xs text-slate-500 pt-1">
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-rose-500" /> Hyderabad, Telangana</span>
                    <span className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-yellow-500" /> 25+ Years Experience (Since 2000)</span>
                    <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> 10,000+ Families Guided</span>
                  </div>
                </div>
              </div>

              {/* Embedded Callback Form Container */}
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-xl space-y-6">
                <div className="text-center max-w-xl mx-auto space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-primary block">Direct Inquiry Channel</span>
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
                    Request a Personalized Callback
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500">
                    Submit your family's basic parameters. We coordinate with you at a time convenient for your international time zone.
                  </p>
                </div>

                <CallbackForm source="nri_blog_bottom_embedded" />
              </div>

              {/* Educational Disclaimer */}
              <div className="p-6 bg-slate-100 dark:bg-slate-900/60 rounded-3xl border border-slate-200 dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400 space-y-2">
                <div className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Statutory & Regulatory Notice
                </div>
                <p>
                  VSR Insurance Plus is an independent insurance advisory consultancy led by licensed advisor V. S. Reddy. We represent Life Insurance Corporation of India (LIC) and HDFC ERGO General Insurance Company Ltd. VSR Insurance Plus is not the official corporate website of LIC or HDFC ERGO.
                </p>
                <p>
                  Insurance is the subject matter of solicitation. Policy terms, bonuses, waiting periods, and underwriting acceptance are strictly determined by the official sales literature and policy bonds issued by the respective insurer. For legal and privacy terms, review our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> and <Link to="/terms" className="text-primary underline">Terms of Service</Link>.
                </p>
              </div>
            </section>
          </article>
        </div>
      </div>

      {/* Sticky Mobile Floating Action Bar */}
      <aside aria-label="Mobile callback action" className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 p-3 shadow-2xl">
        <div className="flex items-center justify-between gap-3 max-w-md mx-auto">
          <div>
            <div className="text-xs font-extrabold text-slate-900 dark:text-white">NRI Insurance Guidance</div>
            <div className="text-[10px] text-slate-500">Free advisor review • V. S. Reddy</div>
          </div>
          <button
            onClick={() => openCallbackModal('mobile_sticky_bar')}
            className="px-5 py-2.5 bg-primary hover:bg-blue-600 text-white rounded-xl text-xs font-bold shadow-md shadow-primary/20 flex items-center gap-1.5 flex-shrink-0 cursor-pointer"
          >
            <PhoneCall className="w-3.5 h-3.5" /> Request Callback
          </button>
        </div>
      </aside>

      {/* Reusable Callback Modal */}
      <CallbackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default NRIInsuranceLeadBlog;
